import { Bell, FileText, LayoutDashboard, Users } from 'lucide-react';
import logoCharte from '../../assets/top-formation.png';
import { initialesDuNom, styleDesNuances, type MarquePublique } from '../../marque/marque';

/**
 * Aperçu de la marque (TF-436) : un extrait de l'interface — menu, tableau de
 * bord, boutons, page de connexion, e-mail — rendu avec les vraies classes de
 * la charte, dans un cadre qui porte les nuances du brouillon. Rien hors du
 * cadre ne change.
 */
export function ApercuInterface({ marque }: { marque: MarquePublique }): JSX.Element {
  const logo = marque.logo?.url ?? logoCharte;
  return (
    <div style={styleDesNuances(marque.nuances)} className="space-y-4" data-testid="apercu-marque">
      {/* Espace de travail : menu et tableau de bord */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-codialis-cream">
        <div className="grid grid-cols-1 sm:grid-cols-[200px_minmax(0,1fr)]">
          <div className="min-w-0 border-b border-gray-200 bg-white sm:border-b-0 sm:border-r">
            <div className="border-b border-gray-100 px-4 py-3">
              <img src={logo} alt={marque.nom} className="h-6 w-auto max-w-full object-contain" />
            </div>
            <div className="px-3 py-3">
              <div className="flex items-center gap-2 rounded-xl bg-codialis-sand p-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-codialis-mint to-codialis-teal text-[11px] font-bold text-white">
                  {initialesDuNom(marque.nom)}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-xs font-semibold text-codialis-navy">{marque.nom}</div>
                  <div className="truncate text-[10px] text-codialis-gray">Direction</div>
                </div>
              </div>
            </div>
            <div className="space-y-0.5 px-2 pb-3">
              <div className="nav-link active">
                <LayoutDashboard className="nav-icon h-4 w-4" aria-hidden="true" />
                <span className="truncate">Tableau de bord</span>
              </div>
              <div className="nav-link">
                <Users className="h-4 w-4 text-codialis-gray" aria-hidden="true" />
                <span className="truncate">Apprenants</span>
                <span className="badge badge-mint ml-auto">12</span>
              </div>
              <div className="nav-link">
                <FileText className="h-4 w-4 text-codialis-gray" aria-hidden="true" />
                <span className="truncate">Documents</span>
              </div>
            </div>
          </div>

          <div className="min-w-0 space-y-3 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display truncate text-base font-bold text-codialis-navy">Tableau de bord</div>
                <div className="truncate text-[11px] text-codialis-gray">Antenne EST · aujourd’hui</div>
              </div>
              <Bell className="h-5 w-5 shrink-0 text-codialis-navy" aria-hidden="true" />
            </div>
            <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2">
              <div className="gradient-card rounded-xl p-3 text-white">
                <div className="text-[10px] uppercase tracking-wide text-white/70">Apprenants actifs</div>
                <div className="font-display text-2xl font-extrabold">128</div>
                <div className="text-[11px] font-semibold text-codialis-mint">dont 6 entrées ce mois</div>
              </div>
              <div className="rounded-xl bg-white p-3 shadow-card">
                <div className="text-[10px] uppercase tracking-wide text-codialis-gray">Taux de présence</div>
                <div className="font-display text-2xl font-extrabold text-codialis-navy">94 %</div>
                <div className="progress-track mt-2 h-1.5 overflow-hidden rounded-full">
                  <div className="progress-bar h-full rounded-full" style={{ width: '94%' }} />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex min-h-[36px] items-center rounded-xl bg-codialis-navy px-3 text-xs font-semibold text-white">
                Enregistrer
              </span>
              <span className="gradient-mint inline-flex min-h-[36px] items-center rounded-xl px-3 text-xs font-semibold text-codialis-navy">
                Valider
              </span>
              <span className="text-xs font-semibold text-codialis-sky underline">Ouvrir le dossier</span>
              <span className="badge badge-mint">Validé</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Page de connexion */}
        <div className="login-bg min-w-0 rounded-2xl p-4">
          <div className="inline-flex rounded-xl bg-white px-3 py-2">
            <img src={logo} alt={marque.nom} className="h-6 w-auto max-w-full object-contain" />
          </div>
          <div className="mt-3 font-display text-lg font-extrabold leading-tight text-white">
            La plateforme de gestion
            <br />
            <span className="text-codialis-mint">de votre CFA et de votre OF.</span>
          </div>
          <div className="mt-3 rounded-xl bg-white p-3">
            <div className="font-display text-sm font-bold text-codialis-navy">Bienvenue.</div>
            <div className="mt-2 flex min-h-[36px] items-center justify-center rounded-xl bg-codialis-navy text-xs font-semibold text-white">
              Se connecter
            </div>
            <div className="mt-2 text-center text-[10px] text-codialis-gray">{marque.nom} · Données hébergées en France</div>
          </div>
        </div>

        {/* E-mail */}
        <div className="min-w-0 rounded-2xl border border-gray-200 bg-white p-4 font-sans">
          <div className="text-[10px] uppercase tracking-wide text-codialis-gray">E-mail envoyé</div>
          <div className="mt-2 text-xs font-bold text-codialis-navy">{marque.nom}</div>
          <div className="mt-2 text-sm font-bold text-codialis-navy">Votre espace apprenant est prêt</div>
          <p className="mt-1 text-xs text-codialis-ink">Bonjour, vos documents sont disponibles.</p>
          <p className="mt-2 text-xs font-bold text-codialis-blue underline">Ouvrir mes documents</p>
          <p className="mt-3 text-[10px] text-codialis-gray">
            Message automatique, envoyé par la plateforme de gestion de {marque.nom}.
          </p>
        </div>
      </div>
    </div>
  );
}
